import styled from "@emotion/styled";
import {
  IconEthereum,
  IconSolana,
  IconApecoin,
  IconAvalanche,
  IconCelo,
  IconCardano,
  IconFlow,
  IconImx,
  IconKlaytn,
  IconKusama,
  IconOmnichain,
  IconPolygon,
  IconTezos,
} from "./icons";

type UnitMap = {
  [key: string]: {
    Icon: typeof IconEthereum;
    abbr: string;
  };
};

const unitMap: UnitMap = {
  apecoin: {
    Icon: IconApecoin,
    abbr: "APE",
  },
  avalanche: {
    Icon: IconAvalanche,
    abbr: "AVAX",
  },
  cardano: {
    Icon: IconCardano,
    abbr: "ADA",
  },
  celo: {
    Icon: IconCelo,
    abbr: "CELO",
  },
  ethereum: {
    Icon: IconEthereum,
    abbr: "ETH",
  },
  flow: {
    Icon: IconFlow,
    abbr: "FLOW",
  },
  imx: {
    Icon: IconImx,
    abbr: "IMX",
  },
  klaytn: {
    Icon: IconKlaytn,
    abbr: "KLAY",
  },
  kusama: {
    Icon: IconKusama,
    abbr: "KSM",
  },
  omnichain: {
    Icon: IconOmnichain,
    abbr: "Omnichain",
  },
  polygon: {
    Icon: IconPolygon,
    abbr: "MATIC",
  },
  solana: {
    Icon: IconSolana,
    abbr: "SOL",
  },
  "ethereum on polygon": {
    Icon: IconEthereum,
    abbr: "ETH",
  },
  tezos: {
    Icon: IconTezos,
    abbr: "XTZ",
  },
};

const getUnit = (unit: string) => {
  const mappedItem = unitMap[unit.toLowerCase()];
  return mappedItem ?? { Icon: null, abbr: null };
};

/**
 * Given a unit, return the chain's SVG Icon
 */
function CurrencyIcon({ unit, ...props }: { unit: string }) {
  const { Icon, abbr } = getUnit(unit);
  return Icon ? (
    <Icon aria-label={abbr} title={abbr} role="img" {...props} />
  ) : null;
}

interface CurrencyProps {
  value?: number | string;
  unit?: string;
  defaultValue?: string;
}

/**
 * Given a value and unit, output value prefixed with currency SVG
 *
 * If unit is not recognized, output value suffixed with given unit string
 * NOTE: for accessibility notes, see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/img_role#svg_and_roleimg
 */
function Currency({ value, unit = "", defaultValue = "-" }: CurrencyProps) {
  const { abbr } = getUnit(unit);

  return (
    <SCurrency>
      <CurrencyIcon unit={unit} />
      {value ?? defaultValue}
      {!abbr && <span>{unit}</span>}
    </SCurrency>
  );
}

const SCurrency = styled.div({
  alignItems: "center",
  display: "inline-flex",
  svg: {
    marginRight: ".33em",
    width: "1em",
  },
  span: {
    alignSelf: "flex-end",
    fontSize: ".6em",
    marginBottom: ".1em",
    opacity: ".7",
    textTransform: "uppercase",
  },
});

export { Currency, CurrencyIcon };
